import { memo, useMemo } from "react";
import { TooltipHost } from "@fluentui/react";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../Constants/AppRoutes";
import { ISensor } from "../../Models/ISensor";
import { ISentinelRich } from "../../Models/ISentinel";
import { isSensorOffline } from "../../Utils/SensorStatus";
import IconButtonItem from "../IconButtonItem/IconButtonItem";
import "./MonitoringCardGateway.scss";

export interface IMonitoringCardGatewayProps {
  sentinel: ISentinelRich;
  index: number;
}

// OTTIMIZZATO: Memoizza il componente BatteryDisplay
const BatteryDisplay = memo((props: { charge: number }) => {
  return (
    <span className="battery-icon-inline">
      {props.charge === 4 && (
        <img src="./assets/power_plug.svg" alt="Alimentato dal cavo" title="Alimentato dal cavo" />
      )}
      {props.charge === 3 && (
        <img src="./assets/battery_full.png" alt="Full" />
      )}
      {props.charge === 2 && (
        <img src="./assets/battery_mid.png" alt="Mid" />
      )}
      {props.charge === 1 && (
        <img src="./assets/battery_low.png" alt="Low" />
      )}
      {props.charge === 0 && (
        <img src="./assets/battery_dead.png" alt="Dead" />
      )}
    </span>
  );
});

BatteryDisplay.displayName = "BatteryDisplay";

const MonitoringCardGateway = (props: IMonitoringCardGatewayProps) => {
  const navigate = useNavigate();
  
  // OTTIMIZZATO: Memoizza la ricerca del sensore porta
  const doorSensor = useMemo(
    () => props.sentinel.sensors.find((x: ISensor) => x.type === "door"),
    [props.sentinel.sensors]
  );

  // Verifica se mostrare la stanza
  const shouldShowRoom = useMemo(() => {
    const room = props.sentinel.patient.room;
    return room && room !== "0" && String(room).trim() !== "";
  }, [props.sentinel.patient.room]);

  // OTTIMIZZATO: Memoizza i calcoli derivati dal sensore
  const sensorData = useMemo(() => {
    if (!doorSensor) return null;

    // Il subscriber MQTT manda in lastValue SOLO lo stato della porta (0/1: cosi' il backend vede la
    // transizione chiusa->aperta) e mette il livello batteria (0..3) in threshold tramite la rotta
    // keepalive. Leggere la batteria da lastValue/10 dava sempre "scarica" (13/09).
    const rawValue = +(doorSensor.lastValue ?? 0);
    const batteryLevel = doorSensor.threshold == null ? 3 : Math.min(4, Math.max(0, +doorSensor.threshold)); // 0-3 batteria, 4 = cavo
    const doorState = rawValue % 10; // 0 = chiusa, 1 = aperta (tollera vecchi valori combinati)
    const lastUpdate = doorSensor.updatedAt;
    const isOnline = !isSensorOffline(doorSensor);

    return { batteryLevel, doorState, lastUpdate, isOnline };
  }, [doorSensor]);

  if (!doorSensor || !sensorData) {
    return (
      <div className="gateway-card-wrapper">
        <div className="gateway-card-error">
          <strong>{props.sentinel.patient.identifier}</strong>
          <span> - Sensore porta non disponibile</span>
        </div>
      </div>
    );
  }

  const { batteryLevel, doorState, lastUpdate, isOnline } = sensorData;

  return (
    <div className="gateway-card-wrapper">
      <div className="gateway-card-content">
        <div className="gateway-card-icon">
          {!isOnline && (
            <TooltipHost
              content="Sensore offline - Verificare alimentazione, connessione WiFi o malfunzionamento"
              id={`gateway_offline_icon_${props.sentinel.id}`}
            >
              <div className="gateway-card-warning-badge-icon">
                <span className="warning-triangle">⚠️</span>
              </div>
            </TooltipHost>
          )}
          <img src="./assets/exit-room.png" alt="Porta" />
        </div>

        <div className="gateway-card-info">
          <div className="gateway-card-settings-button">
            <IconButtonItem
              iconName="Settings"
              tooltip="Configura Varco"
              onClick={() => {
                navigate(
                  AppRoutes.APP_CONFIGURE_PAGE +
                    "?sentinelId=" +
                    props.sentinel.id
                );
              }}
            />
          </div>
          <div className="gateway-card-row gateway-card-row-top">
            <div className="gateway-card-name">
              <strong>{props.sentinel.patient.identifier}</strong>
            </div>
            {shouldShowRoom && (
              <div className="gateway-card-location">
                Stanza: {String(props.sentinel.patient.room).padStart(2, "0")}
              </div>
            )}
          </div>

          <div className="gateway-card-row gateway-card-row-bottom">
            <div className="gateway-card-item">
              <BatteryDisplay charge={batteryLevel} />
            </div>
            <div className="gateway-card-item">
              <span className={doorState === 1 ? "door-open" : "door-closed"}>
                {doorState === 1 ? "Aperta" : "Chiusa"}
              </span>
            </div>
            <div className="gateway-card-item gateway-card-date">
              {new Date(lastUpdate).toLocaleDateString("it-IT", {
                day: "2-digit",
                month: "2-digit",
              })}{" "}
              {new Date(lastUpdate).toLocaleTimeString("it-IT", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
            <div className="gateway-card-item">
              ID Varco: {props.sentinel.id}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Esporta il componente senza memoizzazione personalizzata
// per permettere il re-render quando il tempo passa e lo stato offline cambia
export default MonitoringCardGateway;
